"use client";

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { ArrowRight, Link, Zap } from "lucide-react";

import { Badge } from "components/ui/badge";

import { Button } from "components/ui/button";

import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";



interface TimelineItem {

  id: number;

  title: string;

  date: string;

  content: string;

  category: string;

  icon: React.ElementType;

  relatedIds: number[];

  status: "completed" | "in-progress" | "pending";

  energy: number;

  cta?: {
    label: string;
    to: string;
    description?: string;
    disabled?: boolean;
    variant?: "default" | "secondary" | "outline";
  };

}



interface RadialOrbitalTimelineProps {

  timelineData: TimelineItem[];

}



export default function RadialOrbitalTimeline({

  timelineData,

}: RadialOrbitalTimelineProps) {

  const navigate = useNavigate();

  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>(

    {}

  );

  const [viewMode, setViewMode] = useState<"orbital">("orbital");

  const [rotationAngle, setRotationAngle] = useState<number>(0);

  const [autoRotate, setAutoRotate] = useState<boolean>(true);

  const [pulseEffect, setPulseEffect] = useState<Record<number, boolean>>({});

  const [centerOffset, setCenterOffset] = useState<{ x: number; y: number }>({

    x: 0,

    y: 0,

  });

  const [activeNodeId, setActiveNodeId] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const orbitRef = useRef<HTMLDivElement>(null);

  const nodeRefs = useRef<Record<number, HTMLDivElement | null>>({});



  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {

    if (e.target === containerRef.current || e.target === orbitRef.current) {

      setExpandedItems({});

      setActiveNodeId(null);

      setPulseEffect({});

      setAutoRotate(true);

    }

  };



  const toggleItem = (id: number) => {

    setExpandedItems((prev) => {

      const newState = { ...prev };

      Object.keys(newState).forEach((key) => {

        if (parseInt(key) !== id) {

          newState[parseInt(key)] = false;

        }

      });



      newState[id] = !prev[id];



      if (!prev[id]) {

        setActiveNodeId(id);

        setAutoRotate(false);



        const relatedItems = getRelatedItems(id);

        const newPulseEffect: Record<number, boolean> = {};

        relatedItems.forEach((relId) => {

          newPulseEffect[relId] = true;

        });

        setPulseEffect(newPulseEffect);



        centerViewOnNode(id);

      } else {

        setActiveNodeId(null);

        setAutoRotate(true);

        setPulseEffect({});

      }



      return newState;

    });

  };



  useEffect(() => {

    let rotationTimer: NodeJS.Timeout;



    if (autoRotate && viewMode === "orbital") {

      rotationTimer = setInterval(() => {

        setRotationAngle((prev) => {

          const newAngle = (prev + 0.3) % 360;

          return Number(newAngle.toFixed(3));

        });

      }, 50);

    }



    return () => {

      if (rotationTimer) {

        clearInterval(rotationTimer);

      }

    };

  }, [autoRotate, viewMode]);



  const centerViewOnNode = (nodeId: number) => {

    if (viewMode !== "orbital" || !nodeRefs.current[nodeId]) return;



    const nodeIndex = timelineData.findIndex((item) => item.id === nodeId);

    const totalNodes = timelineData.length;

    const targetAngle = (nodeIndex / totalNodes) * 360;



    setRotationAngle(270 - targetAngle);

  };



  const calculateNodePosition = (index: number, total: number) => {

    const angle = ((index / total) * 360 + rotationAngle) % 360;

    const radius = 200;

    const radian = (angle * Math.PI) / 180;



    const x = radius * Math.cos(radian) + centerOffset.x;

    const y = radius * Math.sin(radian) + centerOffset.y;



    const zIndex = Math.round(100 + 50 * Math.cos(radian));

    const opacity = Math.max(

      0.4,

      Math.min(1, 0.4 + 0.6 * ((1 + Math.sin(radian)) / 2))

    );



    return { x, y, angle, zIndex, opacity };

  };



  const getRelatedItems = (itemId: number): number[] => {

    const currentItem = timelineData.find((item) => item.id === itemId);

    return currentItem ? currentItem.relatedIds : [];

  };



  const isRelatedToActive = (itemId: number): boolean => {

    if (!activeNodeId) return false;

    const relatedItems = getRelatedItems(activeNodeId);

    return relatedItems.includes(itemId);

  };



  const getStatusStyles = (status: TimelineItem["status"]): string => {

    switch (status) {

      case "completed":

        return "text-white bg-gray-900 border-gray-900";

      case "in-progress":

        return "text-gray-900 bg-white border-gray-900";

      case "pending":

        return "text-gray-700 bg-gray-200/60 border-gray-400/50";

      default:

        return "text-gray-700 bg-gray-200/60 border-gray-400/50";

    }

  };



  return (

    <div

      className="w-full h-screen flex flex-col items-center justify-center bg-transparent overflow-hidden"

      ref={containerRef}

      onClick={handleContainerClick}

    >

      <div className="relative w-full max-w-4xl h-full flex items-center justify-center">

        <div

          className="absolute w-full h-full flex items-center justify-center"

          ref={orbitRef}

          style={{

            perspective: "1000px",

            transform: `translate(${centerOffset.x}px, ${centerOffset.y}px)`,

          }}

        >

          <div className="absolute w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 via-blue-500 to-teal-500 animate-pulse flex items-center justify-center z-10">

            <div className="absolute w-20 h-20 rounded-full border border-gray-400/30 animate-ping opacity-70"></div>

            <div

              className="absolute w-24 h-24 rounded-full border border-gray-400/20 animate-ping opacity-50"

              style={{ animationDelay: "0.5s" }}

            ></div>

            <div className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-md"></div>

          </div>



          <div className="absolute w-96 h-96 rounded-full border border-gray-400/20"></div>



          {timelineData.map((item, index) => {

            const position = calculateNodePosition(index, timelineData.length);

            const isExpanded = expandedItems[item.id];

            const isRelated = isRelatedToActive(item.id);

            const isPulsing = pulseEffect[item.id];

            const Icon = item.icon;



            const nodeStyle = {

              transform: `translate(${position.x}px, ${position.y}px)`,

              zIndex: isExpanded ? 200 : position.zIndex,

              opacity: isExpanded ? 1 : position.opacity,

            };



            return (

              <div

                key={item.id}

                ref={(el) => { nodeRefs.current[item.id] = el; }}

                className="absolute transition-all duration-700 cursor-pointer"

                style={nodeStyle}

                onClick={(e) => {

                  e.stopPropagation();

                  toggleItem(item.id);

                }}

              >

                <div

                  className={`absolute rounded-full -inset-1 ${

                    isPulsing ? "animate-pulse duration-1000" : ""

                  }`}

                  style={{

                    background: `radial-gradient(circle, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 70%)`,

                    width: `${item.energy * 0.5 + 40}px`,

                    height: `${item.energy * 0.5 + 40}px`,

                    left: `-${(item.energy * 0.5 + 40 - 40) / 2}px`,

                    top: `-${(item.energy * 0.5 + 40 - 40) / 2}px`,

                  }}

                ></div>



                <div

                  className={`

                  w-10 h-10 rounded-full flex items-center justify-center

                  ${

                    isExpanded

                      ? "bg-gray-900 text-white"

                      : isRelated

                      ? "bg-gray-700/80 text-white"

                      : "bg-white text-gray-900"

                  }

                  border-2 

                  ${

                    isExpanded

                      ? "border-gray-900 shadow-lg shadow-gray-900/30"

                      : isRelated

                      ? "border-gray-700 animate-pulse"

                      : "border-gray-400/60"

                  }

                  transition-all duration-300 transform

                  ${isExpanded ? "scale-150" : ""}

                `}

                >

                  <Icon size={16} />

                </div>



                <div

                  className={`

                  absolute top-12  whitespace-nowrap

                  text-xs font-semibold tracking-wider

                  transition-all duration-300

                  ${isExpanded ? "text-gray-900 scale-125" : "text-gray-700/80"}

                `}

                >

                  {item.title}

                </div>



                {isExpanded && (

                  <Card className="absolute top-20 left-1/2 -translate-x-1/2 w-80 bg-white/95 backdrop-blur-lg border-gray-300/50 shadow-xl shadow-gray-900/20 overflow-visible">

                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-px h-3 bg-gray-400/60"></div>

                    <CardHeader className="pb-2">

                      <div className="flex justify-between items-center">

                        <Badge
                          className={`px-2 text-xs ${getStatusStyles(
                            item.status
                          )}`}
                        >
                          {item.status === "completed"
                            ? "COMPLETED"
                            : item.status === "in-progress"
                            ? "IN PROGRESS"
                            : "PENDING"}
                        </Badge>

                        <span className="text-xs font-mono text-gray-600/70">

                          {item.date}

                        </span>

                      </div>

                      <CardTitle className="text-sm mt-2">

                        {item.title}

                      </CardTitle>

                    </CardHeader>

                    <CardContent className="text-xs text-gray-700/90">

                      <p>{item.content}</p>

                      {item.cta ? (
                        <div className="mt-4">
                          <Button
                            size="lg"
                            variant={item.cta.variant ?? "default"}
                            className="w-full justify-between"
                            disabled={item.cta.disabled}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(item.cta.to);
                            }}
                          >
                            <span>{item.cta.label}</span>
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                          {item.cta.description ? (
                            <p className="mt-2 text-[11px] text-gray-600/80">
                              {item.cta.description}
                            </p>
                          ) : null}
                        </div>
                      ) : null}



                      <div className="mt-4 pt-3 border-t border-gray-300/30">

                        <div className="flex justify-between items-center text-xs mb-1">

                          <span className="flex items-center">

                            <Zap size={10} className="mr-1" />

                            Energy Level

                          </span>

                          <span className="font-mono">{item.energy}%</span>

                        </div>

                        <div className="w-full h-1 bg-gray-200/60 rounded-full overflow-hidden">

                          <div

                            className="h-full bg-gradient-to-r from-blue-500 to-purple-500"

                            style={{ width: `${item.energy}%` }}

                          ></div>

                        </div>

                      </div>



                      {item.relatedIds.length > 0 && (

                        <div className="mt-4 pt-3 border-t border-gray-300/30">

                          <div className="flex items-center mb-2">

                            <Link size={10} className="text-gray-600/70 mr-1" />

                            <h4 className="text-xs uppercase tracking-wider font-medium text-gray-600/70">

                              Connected Nodes

                            </h4>

                          </div>

                          <div className="flex flex-wrap gap-1">

                            {item.relatedIds.map((relatedId) => {

                              const relatedItem = timelineData.find(

                                (i) => i.id === relatedId

                              );

                              return (

                                <Button

                                  key={relatedId}

                                  variant="outline"

                                  size="sm"

                                  className="flex items-center h-6 px-2 py-0 text-xs rounded-none border-gray-300/40 bg-transparent hover:bg-gray-100/80 text-gray-700/80 hover:text-gray-900 transition-all"

                                  onClick={(e) => {

                                    e.stopPropagation();

                                    toggleItem(relatedId);

                                  }}

                                >

                                  {relatedItem?.title}

                                  <ArrowRight

                                    size={8}

                                    className="ml-1 text-gray-600/60"

                                  />

                                </Button>

                              );

                            })}

                          </div>

                        </div>

                      )}

                    </CardContent>

                  </Card>

                )}

              </div>

            );

          })}

        </div>

      </div>

    </div>

  );

}
